import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { MapPin, Compass, ArrowLeft, ZoomIn } from "lucide-react";

function coverageBand(pct, venueCount) {
  if (venueCount === 0) return { label: "No nominations yet", color: "#7F1D1D", ring: "#450A0A" };
  if (pct === 0) return { label: "No inspections yet", color: "#DC2626", ring: "#7F1D1D" };
  if (pct <= 25) return { label: "Sparse", color: "#F97316", ring: "#9A3412" };
  if (pct <= 50) return { label: "Building", color: "#EAB308", ring: "#854D0E" };
  if (pct <= 75) return { label: "Solid", color: "#22C55E", ring: "#166534" };
  return { label: "Comprehensive", color: "#059669", ring: "#065F46" };
}

const UK_CENTRE = [51.35, -1.9];

// Recentre the map when the visible rows change (drill-in/out)
function FitToRows({ rows, defaultZoom }) {
  const map = useMap();
  useEffect(() => {
    const pts = rows.filter(r => typeof r.lat === "number" && typeof r.lng === "number").map(r => [r.lat, r.lng]);
    if (pts.length === 0) return;
    if (pts.length === 1) map.setView(pts[0], defaultZoom);
    else map.fitBounds(pts, { padding: [40, 40], maxZoom: 12 });
  }, [rows, map, defaultZoom]);
  return null;
}

export default function Coverage() {
  const [params, setParams] = useSearchParams();
  const areaFilter = params.get("area") || "";
  const isDistrict = Boolean(areaFilter);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/coverage${isDistrict ? `?area=${encodeURIComponent(areaFilter)}` : ""}`)
      .then(r => setRows(r.data || []))
      .finally(() => setLoading(false));
  }, [areaFilter, isDistrict]);

  const withCoords = useMemo(
    () => rows.filter(r => typeof r.lat === "number" && typeof r.lng === "number"),
    [rows]
  );
  const gaps = useMemo(() => {
    if (isDistrict) return rows.filter(r => r.venue_count === 0 || r.coverage_pct <= 25);
    return rows.filter(r => r.coverage_pct <= 25);
  }, [rows, isDistrict]);
  const totalVenues = rows.reduce((s, r) => s + r.venue_count, 0);
  const totalInspected = rows.reduce((s, r) => s + r.inspected_count, 0);

  const openDistricts = (areaName) => setParams({ area: areaName });
  const backToAreas = () => setParams({});

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-blue-700 font-semibold">
            <Compass className="w-4 h-4" /> Coverage map
          </div>
          {isDistrict ? (
            <>
              <div className="mt-2 flex items-center gap-2 text-sm text-gray-600" data-testid="coverage-breadcrumbs">
                <button onClick={backToAreas} className="inline-flex items-center gap-1 text-blue-700 hover:underline" data-testid="coverage-back-btn">
                  <ArrowLeft className="w-4 h-4"/> All areas
                </button>
                <span>/</span>
                <span className="font-medium text-gray-900">{areaFilter}</span>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
                {areaFilter} — neighbourhood gaps
              </h1>
              <p className="mt-3 text-gray-700">Coverage broken down by postcode district. Empty and sparse districts are where a nomination will make the biggest difference.</p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-gray-900 mt-2">Where we still need help</h1>
              <p className="mt-3 text-gray-700">See how many venues Access Local has graded in each area. Click any circle to zoom into its postcode districts and see gaps at street level.</p>
            </>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Card className="border border-gray-200 rounded-xl"><CardContent className="p-4"><div className="text-3xl font-heading font-bold text-gray-900" data-testid="coverage-total-venues">{totalVenues}</div><div className="text-xs text-gray-600">venues {isDistrict ? "in " + areaFilter : "on Access Local"}</div></CardContent></Card>
          <Card className="border border-gray-200 rounded-xl"><CardContent className="p-4"><div className="text-3xl font-heading font-bold text-gray-900" data-testid="coverage-total-inspected">{totalInspected}</div><div className="text-xs text-gray-600">inspected so far</div></CardContent></Card>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-[1fr_320px] gap-6">
        <div className="rounded-xl border-2 border-gray-900 overflow-hidden" style={{ height: 560 }} data-testid="coverage-map">
          {!loading && (
            <MapContainer center={UK_CENTRE} zoom={9} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <FitToRows rows={withCoords} defaultZoom={isDistrict ? 12 : 10} />
              {withCoords.map(r => {
                const band = coverageBand(r.coverage_pct, r.venue_count);
                const key = isDistrict ? r.district_code : r.area;
                const radius = isDistrict
                  ? Math.max(10, Math.min(28, 8 + r.venue_count * 3))
                  : Math.max(14, Math.min(48, 12 + r.venue_count * 3));
                return (
                  <CircleMarker
                    key={key}
                    center={[r.lat, r.lng]}
                    radius={radius}
                    pathOptions={{ color: band.ring, weight: 2, fillColor: band.color, fillOpacity: 0.55 }}
                    eventHandlers={!isDistrict ? { click: () => openDistricts(r.area) } : undefined}
                  >
                    <Tooltip direction="top" opacity={0.95}>
                      <div className="text-xs">
                        <div className="font-semibold">{isDistrict ? `${r.district_code} · ${r.district_name}` : r.area}</div>
                        <div>{r.inspected_count}/{r.venue_count} inspected ({r.coverage_pct}%)</div>
                      </div>
                    </Tooltip>
                    <Popup>
                      <div className="min-w-[220px]">
                        <div className="font-heading font-semibold text-base">
                          {isDistrict ? `${r.district_code} — ${r.district_name}` : r.area}
                        </div>
                        <div className="text-xs text-gray-600 mb-2">{band.label} · {r.coverage_pct}% inspected</div>
                        <div className="text-sm">{r.inspected_count} of {r.venue_count} venues graded</div>
                        {r.avg_rating > 0 && <div className="text-xs text-gray-600 mt-1">Average score: {r.avg_rating}/100</div>}
                        <div className="mt-3 flex gap-3 flex-wrap">
                          {!isDistrict && (
                            <button onClick={() => openDistricts(r.area)} className="text-blue-600 font-medium text-sm inline-flex items-center gap-1" data-testid={`popup-zoom-${r.area}`}>
                              <ZoomIn className="w-3.5 h-3.5"/> Zoom into districts
                            </button>
                          )}
                          <Link to={`/search?area=${encodeURIComponent(isDistrict ? r.area : r.area)}`} className="text-gray-700 font-medium text-sm">Search venues →</Link>
                        </div>
                      </div>
                    </Popup>
                  </CircleMarker>
                );
              })}
            </MapContainer>
          )}
        </div>

        <aside className="space-y-4">
          <Card className="border border-gray-200 rounded-xl">
            <CardContent className="p-5">
              <h2 className="font-heading text-lg font-semibold">Colour key</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {[
                  { p: 0, vc: 0, label: "No nominations yet — 0 venues" },
                  { p: 0, vc: 1, label: "No inspections yet — 0%" },
                  { p: 15, vc: 1, label: "Sparse — ≤ 25%" },
                  { p: 40, vc: 1, label: "Building — 26–50%" },
                  { p: 65, vc: 1, label: "Solid — 51–75%" },
                  { p: 90, vc: 1, label: "Comprehensive — 76%+" },
                ].map((row, i) => {
                  const b = coverageBand(row.p, row.vc);
                  return (
                    <li key={i} className="flex items-center gap-3">
                      <span className="w-4 h-4 rounded-full border" style={{ background: b.color, borderColor: b.ring }}/>
                      <span className="text-gray-700">{row.label}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-gray-500">Circle size grows with the number of venues nominated in that {isDistrict ? "district" : "area"}.</p>
            </CardContent>
          </Card>

          <Card className="border-2 border-red-200 bg-red-50 rounded-xl">
            <CardContent className="p-5">
              <h2 className="font-heading text-lg font-semibold text-red-800">Help us fill the gaps</h2>
              {gaps.length === 0 && <p className="text-sm text-red-700 mt-2">Every {isDistrict ? "district" : "area"} here has decent coverage — nice work! Keep nominating to grow it further.</p>}
              {gaps.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="coverage-gaps-list">
                  {gaps.slice(0, 8).map(r => (
                    <li key={isDistrict ? r.district_code : r.area} className="flex items-center justify-between gap-2 text-sm">
                      <span className="inline-flex items-center gap-2 text-red-900">
                        <MapPin className="w-3.5 h-3.5"/>
                        {isDistrict ? `${r.district_code} · ${r.district_name}` : r.area}
                      </span>
                      <span className="text-xs text-red-700">{r.inspected_count}/{r.venue_count}</span>
                    </li>
                  ))}
                </ul>
              )}
              <Button asChild className="mt-4 rounded-full bg-red-600 hover:bg-red-700 w-full" data-testid="coverage-nominate-btn">
                <Link to="/nominate">Nominate a place</Link>
              </Button>
            </CardContent>
          </Card>
        </aside>
      </div>

      {rows.length > 0 && (
        <div className="mt-10">
          <h2 className="font-heading text-2xl font-semibold text-gray-900">
            {isDistrict ? "Postcode districts" : "All areas"}
          </h2>
          <div className="mt-4 overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full text-sm" data-testid="coverage-table">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="px-4 py-3 font-semibold">{isDistrict ? "District" : "Area"}</th>
                  <th className="px-4 py-3 font-semibold">Venues</th>
                  <th className="px-4 py-3 font-semibold">Inspected</th>
                  <th className="px-4 py-3 font-semibold">Coverage</th>
                  <th className="px-4 py-3 font-semibold">Avg score</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const b = coverageBand(r.coverage_pct, r.venue_count);
                  const key = isDistrict ? r.district_code : r.area;
                  return (
                    <tr key={key} className="border-t border-gray-100">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {isDistrict
                          ? <span><span className="font-mono text-xs bg-gray-100 border border-gray-200 rounded px-1.5 py-0.5 mr-2">{r.district_code}</span>{r.district_name}</span>
                          : r.area}
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.venue_count}</td>
                      <td className="px-4 py-3 text-gray-700">{r.inspected_count}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-2">
                          <span className="w-2.5 h-2.5 rounded-full" style={{ background: b.color }}/>
                          <span className="text-gray-800">{r.coverage_pct}% · {b.label}</span>
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{r.avg_rating > 0 ? `${r.avg_rating}/100` : "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {isDistrict
                          ? <Link to={`/search?area=${encodeURIComponent(r.area)}`} className="text-blue-600 font-medium">Search</Link>
                          : <button onClick={() => openDistricts(r.area)} className="text-blue-600 font-medium inline-flex items-center gap-1" data-testid={`zoom-into-${r.area}`}><ZoomIn className="w-3.5 h-3.5"/>Zoom in</button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
